import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Zap, LogOut, Users, DollarSign, Clock, CheckCircle, Loader2, AlertCircle, Shield, Calendar, MessageSquare, ChevronRight, Wallet, ShieldAlert, BarChart3, Settings, Megaphone, Search, Handshake } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import AdminUsersModal from "../components/AdminUsersModal";
import FactoryResetModal from "../components/FactoryResetModal";
import { STATUSES, STATUS_LABELS, STATUS_COLORS } from "../lib/status";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosAuth = () => axios.create({ withCredentials: true, headers: { Authorization: `Bearer ${localStorage.getItem("lf_session") || ""}` } });

const EUR_RATE = 0.92;
const fmtPrice = (p, c) => c === "eur" ? `€${(p * EUR_RATE).toFixed(0)}` : `$${p?.toFixed(0)}`;

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white border border-zinc-200 rounded-2xl p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-8 h-8 bg-zinc-100 rounded-lg flex items-center justify-center"><Icon size={16} className="text-zinc-700" /></div>
      <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{label}</span>
    </div>
    <div className="text-2xl font-extrabold text-zinc-950">{value}</div>
    {sub && <div className="text-xs text-zinc-400 mt-0.5">{sub}</div>}
  </div>
);

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_COLORS[status] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("all");
  const [showAdminUsers, setShowAdminUsers] = useState(false);
  const [showFactoryReset, setShowFactoryReset] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [me, projs, s] = await Promise.all([
        axiosAuth().get(`${API}/auth/me`),
        axiosAuth().get(`${API}/admin/projects?status=${filter}`),
        axiosAuth().get(`${API}/admin/stats`),
      ]);
      setUser(me.data); setProjects(projs.data); setStats(s.data);
    } catch (e) {
      // Only bounce to /admin/login when the session is genuinely rejected.
      // Any other error (500, 502, 520, network) keeps us on the dashboard
      // so we don't trigger the Google-SSO "infinite refresh" symptom.
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem("lf_session");
        navigate("/admin/login", { replace: true });
      } else {
        // Surface the problem instead of silently bouncing.
        // eslint-disable-next-line no-console
        console.error("Admin dashboard load failed (non-auth):", e);
      }
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const handleLogout = async () => {
    try { await axiosAuth().post(`${API}/auth/logout`); } catch { /* ignored */ }
    localStorage.removeItem("lf_session");
    navigate("/admin/login");
  };

  const FILTERS = ["all", ...STATUSES];

  if (loading) return <div className="min-h-screen bg-zinc-50 flex items-center justify-center"><Loader2 size={32} className="animate-spin text-zinc-400" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3">
            <BrandLogo variant="mark" size={32} />
            <span className="font-bold text-zinc-900">FastPrototype.ai Admin</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="btn-nav-finance" onClick={() => navigate("/admin/finance")}
            className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors">
            <Wallet size={14} /> Finance
          </button>
          <button data-testid="btn-nav-promoters" onClick={() => navigate("/admin/promoters")}
            className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors">
            <Megaphone size={14} /> Promoters
          </button>
          <button data-testid="btn-nav-analytics" onClick={() => navigate("/admin/analytics")}
            className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors">
            <BarChart3 size={14} /> Analytics
          </button>
          <button data-testid="btn-nav-settings" onClick={() => navigate("/admin/settings")}
            className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors">
            <Settings size={14} /> Settings
          </button>
          <button data-testid="btn-nav-seo" onClick={() => navigate("/admin/seo")}
            className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors">
            <Search size={14} /> SEO
          </button>
          <button data-testid="btn-manage-admins" onClick={() => setShowAdminUsers(true)}
            className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors">
            <Shield size={14} /> Admins
          </button>
          <div className="hidden sm:block text-right"><div className="text-sm font-semibold text-zinc-900">{user?.name}</div><div className="text-xs text-zinc-500">{user?.email}</div></div>
          <button data-testid="btn-logout" onClick={handleLogout} className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={Users} label="Total" value={stats.total || 0} />
          <StatCard icon={Clock} label="Awaiting Dep." value={stats.awaiting_deposit || 0} />
          <StatCard icon={AlertCircle} label="Active Build" value={(stats.pending || 0) + (stats.ai_magicians || 0) + (stats.human_review || 0) + (stats.pending_feedback || 0) + (stats.applying_feedback || 0)} />
          <StatCard icon={Wallet} label="Final Review" value={stats.final_review || 0} sub="14-day window" />
          <StatCard icon={CheckCircle} label="Delivered" value={stats.delivered || 0} />
          <StatCard icon={DollarSign} label="Revenue" value={`$${(stats.revenue || 0).toFixed(0)}`} sub="USD received" />
        </div>

        <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
          <div className="px-6 pt-5 pb-4 border-b border-zinc-100 flex flex-wrap gap-2">
            {FILTERS.map(f => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${filter === f ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                {f === "all" ? "All Projects" : STATUS_LABELS[f]}
              </button>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-zinc-100">
                {["Client", "Industry", "Assigned", "Delivery", "Due", "Price", "Status", "Created", ""].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">{h}</th>
                ))}
              </tr></thead>
              <tbody data-testid="projects-table">
                {projects.length === 0 ? (
                  <tr><td colSpan={9} className="px-5 py-12 text-center text-zinc-400">No projects found</td></tr>
                ) : projects.map(p => {
                  const dueDate = p.due_date ? new Date(p.due_date) : null;
                  const overdue = dueDate && p.status !== "delivered" && dueDate < new Date();
                  return (
                  <tr key={p.project_id} data-testid={`project-row-${p.project_id}`}
                    className="border-b border-zinc-50 hover:bg-zinc-50 cursor-pointer"
                    onClick={() => navigate(`/admin/projects/${p.project_id}`)}>
                    <td className="px-5 py-4">
                      <div className="font-medium text-zinc-900 flex items-center gap-2 flex-wrap">
                        {p.client_name}
                        {p.unread_message_count > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full" title="Client messages"><MessageSquare size={9} />{p.unread_message_count}</span>
                        )}
                        {p.promoter_code && (
                          <span
                            title={`Referred by promoter: ${p.promoter_code} · +7% markup baked in`}
                            data-testid={`promoter-badge-${p.project_id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full border border-emerald-200">
                            <Handshake size={9} /> {p.promoter_code}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500">{p.client_email}</div>
                    </td>
                    <td className="px-5 py-4 text-zinc-600">{p.industry}</td>
                    <td className="px-5 py-4 text-xs">
                      {p.assigned_to_name ? (
                        <span className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-full font-semibold" data-testid={`assignee-${p.project_id}`}>
                          {p.assigned_to_name.split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase()} {p.assigned_to_name.split(" ")[0]}
                        </span>
                      ) : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-5 py-4 text-zinc-600">{p.delivery_weeks}wk</td>
                    <td className={`px-5 py-4 text-xs ${overdue ? "text-red-600 font-bold" : "text-zinc-600"}`}>
                      <div className="inline-flex items-center gap-1"><Calendar size={11} />{dueDate ? dueDate.toLocaleDateString() : "—"}</div>
                    </td>
                    <td className="px-5 py-4 font-semibold text-zinc-900">{p.proposal ? fmtPrice(p.price_override || p.proposal?.delivery_adjusted_price_usd, p.currency) : "—"}</td>
                    <td className="px-5 py-4"><StatusBadge status={p.status} /></td>
                    <td className="px-5 py-4 text-xs text-zinc-400">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4"><ChevronRight size={16} className="text-zinc-400" /></td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>

        {/* Danger zone — kept visually distinct + at the very bottom */}
        <div className="mt-12 pt-8 border-t border-red-100" data-testid="danger-zone">
          <div className="flex items-start gap-3 bg-red-50/50 border border-red-200 rounded-2xl p-5">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <ShieldAlert size={18} className="text-red-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-red-900 text-sm">Danger zone</h3>
              <p className="text-xs text-red-800/80 mt-0.5 leading-relaxed">
                Factory reset wipes every project, payment, file, promoter, and admin user. A backup snapshot is saved to disk first. After reset, the platform enters bootstrap mode and the configured Google bootstrap admin (Florin) can sign in to recreate the first admin account.
              </p>
              <button onClick={() => setShowFactoryReset(true)} data-testid="btn-open-factory-reset"
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-red-700 text-white text-xs font-bold rounded-full hover:bg-red-800">
                <ShieldAlert size={11} /> Factory reset…
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminUsersModal open={showAdminUsers} onClose={() => setShowAdminUsers(false)} currentUserId={user?.user_id} />
      <FactoryResetModal open={showFactoryReset} onClose={() => setShowFactoryReset(false)}
        onSuccess={() => {
          // After wipe, admin_users is gone — log out + redirect.
          localStorage.removeItem("lf_session");
          window.location.href = "/admin/login";
        }} />
    </div>
  );
}
